/**
 * OnboardingChecklist — first-login activation panel on the dashboard.
 *
 * Shows a 5-step "let's get you set up" card that walks new users from
 * empty dashboard to fully connected hardware. Auto-hides when:
 *   - User explicitly dismisses
 *   - 4 of 5 steps are done (where skipped counts as done)
 *
 * Each step has 3 actions: "Set up now" (deep link to the right page),
 * "Mark done" (manual override), or "Skip" (lets you ignore the step
 * without nagging). The backend auto-detects most steps from the actual
 * data on the user record, so users who connect hardware via the regular
 * Profile flow get progress credit automatically.
 *
 * Visual: emerald progress bar + numbered step rows + dismiss option.
 * Stays sticky at the top of the dashboard until completed/dismissed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Circle, ArrowRight, X, MapPin, Plant, Drop, House, Plug } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const STEP_META = {
  location_confirmed: {
    title: 'Confirm your location',
    body: "We use your lat/lng for live weather, ET₀, dewpoint, and forecast skips.",
    icon: MapPin,
    cta_label: 'Confirm location',
    cta_path: '/profile',
  },
  grass_type_set: {
    title: 'Pick your grass type',
    body: "Bermuda, St. Augustine, Zoysia, or cool-season — drives the whole agronomy stack (MAD %, GDD base, drought floor).",
    icon: Plant,
    cta_label: 'Set grass type',
    cta_path: '/profile',
  },
  ecowitt_connected: {
    title: 'Connect Ecowitt soil probes',
    body: "Buried WH51 probes are the live signal — without them you're in advisory mode (still useful, but no closed-loop auto-fire).",
    icon: Drop,
    cta_label: 'Connect Ecowitt',
    cta_path: '/profile',
  },
  rachio_connected: {
    title: 'Connect Rachio',
    body: "Lets TurfPulse fire schedules with Smart Split, MAD bypass, freeze/wind/rain skips. Without Rachio you'll still see the calls — just no execution.",
    icon: House,
    cta_label: 'Connect Rachio',
    cta_path: '/profile',
  },
  api_token_generated: {
    title: 'Generate Home Assistant API token',
    body: "Optional. Drops your TurfPulse data into HA's REST sensor in 5 min. Skip if you're not running HA.",
    icon: Plug,
    cta_label: 'Generate token',
    cta_path: '/profile',
  },
};

const STEP_ORDER = ['location_confirmed', 'grass_type_set', 'ecowitt_connected', 'rachio_connected', 'api_token_generated'];

export const OnboardingChecklist = () => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch {
      // Silent — likely just unauth, parent handles redirect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStep = async (step, completed, skipped) => {
    setBusy(step);
    try {
      const res = await axios.post(
        `${API_URL}/api/onboarding/progress`,
        { step, completed, skipped },
        { withCredentials: true },
      );
      setProgress(res.data);
      toast.success(skipped ? 'Step skipped' : 'Step marked done');
    } catch {
      toast.error('Could not update step.');
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async () => {
    if (!window.confirm('Hide the onboarding checklist? You can still set up hardware from your Profile any time.')) return;
    try {
      await axios.post(`${API_URL}/api/onboarding/dismiss`, {}, { withCredentials: true });
      setProgress((p) => ({ ...p, is_complete: true, dismissed_at: new Date().toISOString() }));
    } catch {
      toast.error('Dismiss failed.');
    }
  };

  if (loading || !progress) return null;
  if (progress.is_complete) return null;

  const pctComplete = Math.round((progress.completed_count / progress.total) * 100);

  return (
    <Card className="border-emerald-500/40 bg-gradient-to-br from-emerald-500/5 to-emerald-500/0" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              Welcome to TurfPulse — let's get you set up
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {progress.completed_count} of {progress.total} done · {pctComplete}% complete · finish at least 4 to clear this card
            </CardDescription>
            {/* Soft pointer to the calm /getting-started walkthrough — for
                the homeowner who landed deep and wants context before
                clicking through the checklist tasks. */}
            <Link
              to="/getting-started"
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 underline-offset-2 hover:underline"
              data-testid="onboarding-starter-guide-link"
            >
              New here? Read the 5-minute starter guide →
            </Link>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground transition shrink-0"
            data-testid="onboarding-dismiss"
            aria-label="Dismiss onboarding checklist"
          >
            <X size={20} />
          </button>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${pctComplete}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {STEP_ORDER.map((stepId, idx) => {
          const meta = STEP_META[stepId];
          const state = progress.steps?.[stepId] || { done: false, skipped: false };
          const Icon = meta.icon;
          const checked = state.done || state.skipped;
          return (
            <div
              key={stepId}
              className={`flex items-start gap-3 p-3 rounded-lg border transition ${
                checked
                  ? 'border-emerald-500/30 bg-emerald-500/5'
                  : 'border-border bg-background hover:border-emerald-500/40'
              }`}
              data-testid={`onboarding-step-${stepId}`}
            >
              <div className="shrink-0 pt-0.5">
                {checked
                  ? <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                  : <Circle size={22} className="text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon size={16} weight="duotone" className={checked ? 'text-emerald-600' : 'text-muted-foreground'} />
                  <span className={`font-medium text-sm ${checked ? 'line-through text-muted-foreground' : ''}`}>
                    {idx + 1}. {meta.title}
                  </span>
                  {state.skipped && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30">
                      Skipped
                    </span>
                  )}
                  {state.done && state.auto_detected && !state.skipped && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30">
                      Auto-detected
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{meta.body}</p>
                {!checked && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button asChild size="sm" variant="default" className="h-7 text-xs"
                      data-testid={`onboarding-cta-${stepId}`}>
                      <Link to={meta.cta_path}>
                        {meta.cta_label}
                        <ArrowRight size={12} className="ml-1" />
                      </Link>
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => setStep(stepId, true, false)}
                      disabled={busy === stepId}
                      data-testid={`onboarding-mark-done-${stepId}`}>
                      Mark done
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                      onClick={() => setStep(stepId, false, true)}
                      disabled={busy === stepId}
                      data-testid={`onboarding-skip-${stepId}`}>
                      Skip
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
